/**
 * Kairos Merit — canonical dropdown catalogs.
 *
 * Values are the canonical option strings from `docs/cliente/2026-06_scoring-y-formularios/candidate-form.md` and
 * `docs/cliente/2026-06_scoring-y-formularios/get-a-quote-form.md`. Display localization (if ever needed) maps on top
 * of these stable values; the scoring engine keys off them directly.
 *
 * Since M00 the seven master catalogs (positions, departments, languages,
 * nationalities, cities, property types, luxury levels) live in `catalog_items`
 * and the admin edits them in `/catalogs` — that table is the source of truth
 * and the forms read it from `GET /config/catalogs`. The constants below stay
 * as the seed for that table (`merit-config.seed.ts`) and as the forms' offline
 * fallback when the API call fails. The rest of the lists (availability, visa
 * status, statuses…) have no DB catalog and are code-only.
 */

import { ADDON_CATALOG, PLAN_CATALOG } from './plans.catalog';

// ============================================
// CANDIDATE CATALOGS (candidate-form.md)
// ============================================

// Alphabetical (display order), with "Other" pinned last. Full kairosmerit.ae list.
export const LANGUAGES = [
	'Amharic',
	'Arabic',
	'Bengali',
	'Bulgarian',
	'Burmese',
	'Catalan',
	'Croatian',
	'Czech',
	'Danish',
	'Dutch',
	'English',
	'Finnish',
	'French',
	'German',
	'Greek',
	'Gujarati',
	'Hebrew',
	'Hindi',
	'Hungarian',
	'Indonesian',
	'Italian',
	'Japanese',
	'Khmer',
	'Korean',
	'Kurdish',
	'Malay',
	'Malayalam',
	'Mandarin Chinese',
	'Marathi',
	'Nepali',
	'Norwegian',
	'Pashto',
	'Persian (Farsi)',
	'Polish',
	'Portuguese',
	'Punjabi',
	'Romanian',
	'Russian',
	'Serbian',
	'Sinhala',
	'Slovak',
	'Somali',
	'Spanish',
	'Swahili',
	'Swedish',
	'Tagalog (Filipino)',
	'Tamil',
	'Telugu',
	'Thai',
	'Turkish',
	'Ukrainian',
	'Urdu',
	'Vietnamese',
	'Other',
] as const;

/**
 * CEFR proficiency levels offered per selected language. The scoring engine keys
 * off these codes (see `candidate-scoring.default.ts`). Display hints (Basic…
 * Native) live in i18n; A2 is the floor we let candidates self-report.
 */
export const LANGUAGE_LEVELS = ['A2', 'B1', 'B2', 'C1', 'C2'] as const;

export const AVAILABILITY = ['Immediately', 'One Week', 'Two Weeks', 'One month notice', 'Negotiable'] as const;

/** Shared base of hospitality positions used by both last/target dropdowns. */
const POSITIONS_BASE = [
	'Receptionist',
	'Front Desk Agent',
	'Guest Relations Officer',
	'Concierge',
	'Waiter/Waitress',
	'Head Waiter',
	'Hostess',
	'Bartender',
	'Barista',
	'Commis Chef',
	'Demi Chef de Partie',
	'Chef de Partie',
	'Front Office Supervisor',
	'F&B Supervisor',
	'Assistant Restaurant Manager',
	'Sous Chef',
	'Restaurant Manager',
	'Hotel Manager',
	'Operations Manager',
	'Executive Chef',
	'Director of Rooms',
	'Director of F&B',
	'HR Manager (Hospitality)',
] as const;

/**
 * "No current role" answers offered by the last-position dropdown on top of the
 * `positions` catalog. They are not positions, so they have no catalog row: the
 * form prepends them to whatever the API returns.
 */
export const NO_ROLE_POSITIONS = ['Not currently working', 'No hospitality experience'] as const;

/** Last position: adds the two "no current role" options up front. */
export const LAST_POSITIONS = [...NO_ROLE_POSITIONS, ...POSITIONS_BASE, 'Spa Therapist', 'Artist', 'Other'] as const;

/** Target position: base list + Sales Manager, no "no role" options. */
export const TARGET_POSITIONS = [
	'Receptionist',
	'Front Desk Agent',
	'Guest Relations Officer',
	'Concierge',
	'Waiter/Waitress',
	'Head Waiter',
	'Hostess',
	'Bartender',
	'Barista',
	'Commis Chef',
	'Demi Chef de Partie',
	'Chef de Partie',
	'Front Office Supervisor',
	'F&B Supervisor',
	'Assistant Restaurant Manager',
	'Sous Chef',
	'Restaurant Manager',
	'Hotel Manager',
	'Operations Manager',
	'Executive Chef',
	'Director of Rooms',
	'Sales Manager',
	'Director of F&B',
	'HR Manager (Hospitality)',
	'Spa Therapist',
	'Artist',
	'Other',
] as const;

/**
 * Tipo de contrato del brief de vacante (E-P1-01, y el punto 9 del checklist
 * del 11/10). Solo INFORMA (respuesta 3 del 02/09): el candidato no declara
 * qué contrato busca, así que no hay con qué cruzarlo — se enseña, no descarta.
 *
 * Indefinido / temporal / temporada. La jornada (completa o parcial) es otro
 * campo, `WORK_SCHEDULES`: antes los dos vivían en esta misma lista.
 */
export const CONTRACT_TYPES = ['Permanent', 'Temporary', 'Seasonal'] as const;

/** Jornada, aparte del tipo de contrato. También solo informa. */
export const WORK_SCHEDULES = ['Full-time', 'Part-time'] as const;

/** Meses de un contrato temporal o de temporada. Indefinido no lleva duración. */
export const CONTRACT_DURATION_MONTHS = { min: 1, max: 60 } as const;

export function contractHasDuration(contractType: string | null | undefined): boolean {
	return contractType === 'Temporary' || contractType === 'Seasonal';
}

export const YEARS_OF_EXPERIENCE = ['0–1 years', '1–3 years', '3–5 years', '5+ years'] as const;

/**
 * El suelo numérico de un tramo: «3–5 years» → 3, «5+ years» → 5 (12/09).
 *
 * La vacante pedía los años mínimos DOS veces: una casilla numérica libre y
 * este desplegable, con etiquetas casi iguales — es el «2 duplicado» que vio la
 * clienta. Se quedó el desplegable, y el número que el cruce necesita sale de
 * aquí. El candidato **no guarda un número**: guarda uno de estos cuatro
 * tramos, así que pedir «mínimo 4 años» era una precisión que el dato no tiene.
 *
 * Es el mismo criterio que `parseYears` aplica en el motor de cruce sobre el
 * tramo del candidato, y por eso los dos lados siguen comparando lo mismo.
 */
export function yearsFloor(band: string | null | undefined): number | null {
	if (!band) return null;
	const match = band.match(/\d+/);
	return match ? Number(match[0]) : null;
}

export const DRIVING_LICENCE = ['No Driving License', 'UAE License', 'International License'] as const;

/**
 * Los cinco tramos de salario del CANDIDATO, en dirhams al mes.
 *
 * El candidato dice en cuál espera cobrar. Antes la vacante usaba esta misma
 * lista; desde el 05/10 la oferta va en importes de 500 y esta escala se queda
 * solo en el perfil.
 */
export const SALARY_BRACKETS = [
	'3,000 – 6,000',
	'6,000 – 10,000',
	'10,000 – 15,000',
	'15,000 – 20,000',
	'+20,000',
] as const;

/**
 * Lo que puede pedir un CANDIDATO: los cinco tramos, sin «negociable».
 *
 * Hasta el 05/10 el desplegable lo ofrecía (decisión del 25/08: la persona
 * podía decir «me adapto»). Se quitó de la expectativa. Quien ya lo tiene
 * guardado lo conserva en la ficha; al editar el campo tiene que elegir un
 * tramo. La disponibilidad sigue teniendo su propio «Negotiable», que es el
 * plazo de preaviso, no el sueldo.
 */
export const SALARY_EXPECTATIONS = SALARY_BRACKETS;

/** Un tramo vigente, o vacío si el valor es «Negotiable» u otra cosa retirada. */
export function offeredSalaryExpectation(value: string | null | undefined): string {
	return value && (SALARY_EXPECTATIONS as readonly string[]).includes(value) ? value : '';
}

/**
 * Sueldo que ofrece una VACANTE, en AED al mes: de 500 a 50.000, de 500 en
 * 500, y «+50,000» por encima.
 *
 * Hasta el 05/10 el desplegable repetía los cinco tramos del candidato
 * (3.000–6.000, 6.000–10.000…). La empresa no podía publicar 5.500, 6.000 o
 * 6.500. El candidato sigue con esos cinco: su expectativa no es el sueldo de
 * una oferta, y el cruce compara el punto medio de su tramo con el importe
 * elegido aquí.
 *
 * Los cinco anchos siguen valiendo en la API. Las vacantes ya guardadas los
 * tienen escritos y se editan sin obligar a reescribirlos.
 */
const VACANCY_SALARY_FROM = 500;
const VACANCY_SALARY_TO = 50_000;
const VACANCY_SALARY_STEP = 500;

/** `5500` → `5,500`. Fijo, sin depender del locale del proceso. */
function formatAed(amount: number): string {
	return String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function vacancySalaryScale(): readonly [string, ...string[]] {
	const values: string[] = [];
	for (let amount = VACANCY_SALARY_FROM; amount <= VACANCY_SALARY_TO; amount += VACANCY_SALARY_STEP) {
		values.push(formatAed(amount));
	}
	values.push(`+${formatAed(VACANCY_SALARY_TO)}`);
	return values as unknown as readonly [string, ...string[]];
}

/** Opciones del desplegable al crear o editar una vacante. */
export const VACANCY_SALARY_RANGES = vacancySalaryScale();

/**
 * Lo que la API acepta: la escala de 500 y los cinco tramos anchos ya
 * guardados en vacantes anteriores.
 */
export const ACCEPTED_VACANCY_SALARY_RANGES = [...VACANCY_SALARY_RANGES, ...SALARY_BRACKETS] as unknown as readonly [
	string,
	...string[],
];

const VACANCY_SALARY_POINTS = new Set<string>(VACANCY_SALARY_RANGES);

/**
 * Importe exacto del desplegable nuevo → mínimo y máximo iguales, que es lo
 * que el cruce compara. «+50,000» y un tramo ancho antiguo no se traducen:
 * el primero no tiene techo, y el segundo ya tiene sus números guardados.
 */
export function vacancySalaryBounds(value: string | null | undefined): { min: number; max: number } | null {
	if (!value || value.startsWith('+') || !VACANCY_SALARY_POINTS.has(value)) return null;
	const amount = Number(value.replace(/,/g, ''));
	return Number.isFinite(amount) ? { min: amount, max: amount } : null;
}

/** Orden del filtro de salario del candidato: el primer número, y el «+» detrás del mismo importe. */
export function salaryRangeSortKey(value: string): number {
	const match = value.replace(/,/g, '').match(/\d+/);
	const floor = match ? Number(match[0]) : Number.POSITIVE_INFINITY;
	return value.trim().startsWith('+') ? floor + 1 : floor;
}

/**
 * Visado. **UN solo catálogo para la vacante y para el candidato** (spec del
 * 25/08: fuera `Golden visa` e `Indiferente`, quedan estas tres).
 *
 * Que sea uno solo es el cambio, no un detalle de implementación. Hasta hoy
 * había dos vocabularios distintos —el candidato declaraba `Residence visa` /
 * `Visit visa` / `Cancelled visa` / `Outside UAE`, la vacante pedía `Employment
 * visa provided` / `Any visa status` / `Golden visa`— que no compartían ni un
 * valor salvo `Golden visa`. El cruce tenía entonces que **adivinar la
 * correspondencia**: `vacancy-fit` solo sabía resolver la rama dorada y para
 * todo lo demás emitía un «acepta cualquiera» que no miraba al candidato,
 * mientras que el criterio `workStatusUae` del matching comparaba cadenas de un
 * catálogo contra cadenas del otro y por tanto **nunca podía coincidir** salvo
 * en esa única palabra compartida. Con vocabulario común la comparación es
 * igualdad de cadenas y no hay nada que inferir.
 *
 * Las tres opciones responden a una sola pregunta: **quién paga el visado**.
 *
 * - `Own visa` — lo tiene a su nombre (residencia propia, freelance, inversor…).
 * - `Family visa` — lo tiene por un familiar que lo patrocina.
 * - `Employment visa provided` — lo pone el empleador.
 *
 * Las dos primeras significan lo mismo para la propiedad (no hay visado que
 * tramitar) y se distinguen porque legalmente no son iguales: un visado
 * familiar suele necesitar permiso de trabajo aparte.
 */
//
// Dos más desde el 04/09, a petición de la clienta, para quien AÚN no tiene
// visado: **fuera de EAU** o **de visita**. Las dos responden lo mismo a la
// pregunta de fondo —lo tramita el empleador— y por eso encajan y descartan
// exactamente como `Employment visa provided`; se distinguen porque a la
// propiedad le importa si además hay que traer a la persona.
export const VISA_OPTIONS = [
	'Own visa',
	'Family visa',
	'Employment visa provided',
	'Visit visa',
	'Outside UAE',
] as const;

export type VisaOption = (typeof VISA_OPTIONS)[number];

/**
 * Los dos visados que trae ya puesto el candidato. Es la clase que de verdad
 * usa el cruce: para el empleador «propio» y «familiar» son el mismo caso —no
 * hay nada que patrocinar—, así que una vacante que pide uno no puede descartar
 * a quien trae el otro sin inventarse un requisito que nadie ha pedido.
 */
export const SELF_SPONSORED_VISAS: readonly VisaOption[] = ['Own visa', 'Family visa'];

/** Los que necesitan que la propiedad tramite el visado: todo lo que no es propio. */
export const SPONSORSHIP_NEEDED_VISAS: readonly VisaOption[] = [
	'Employment visa provided',
	'Visit visa',
	'Outside UAE',
];

export const LUXURY_LEVEL = [
	'No Luxury Hospitality Level',
	'3–4 Star Hospitality Level',
	'4–5 Star Hospitality Level',
	'Ultra Luxury Hospitality Level',
] as const;

export const MANAGEMENT_EXPERIENCE = [
	'No Management Experience',
	'Supervisor Level',
	'Assistant Manager',
	'Manager',
	'Director Level',
] as const;

export const GCC_EXPERIENCE = [
	'No GCC Experience',
	'UAE Experience',
	'Saudi Arabia Experience',
	'Qatar Experience',
	'Multiple GCC Countries',
] as const;

/**
 * GCC + Europe locations for the candidate's Current Location (single) and Location
 * Targeting (multiple) autocompletes. 129 locations across the six GCC countries
 * — the canonical list shared with the public web form, so both stay in sync.
 *
 * Each value is a stable `"Country — City"` string the scoring/display layer keys
 * off directly (no i18n needed; the value is shown as-is). The country prefix also
 * disambiguates cities that repeat across countries (e.g. Liwa, Al Ruwais) and lets
 * the dropdown filter match on either country or city. `Other` is pinned last and,
 * when picked, reveals the free Country + City inputs on the form.
 */
export const GCC_LOCATIONS = [
	// Bahrain (13)
	"Bahrain — A'ali",
	'Bahrain — Al Hidd',
	'Bahrain — Budaiya',
	'Bahrain — Durrat Al Bahrain',
	'Bahrain — Hamad Town',
	'Bahrain — Isa Town',
	'Bahrain — Jidhafs',
	'Bahrain — Manama',
	'Bahrain — Muharraq',
	'Bahrain — Riffa',
	'Bahrain — Saar',
	'Bahrain — Sitra',
	'Bahrain — Zallaq',
	// Kuwait (18)
	'Kuwait — Abu Halifa',
	'Kuwait — Al Ahmadi',
	'Kuwait — Al Jahra',
	'Kuwait — Al Qurain',
	'Kuwait — Fahaheel',
	'Kuwait — Farwaniya',
	'Kuwait — Fintas',
	'Kuwait — Hawalli',
	'Kuwait — Jleeb Al-Shuyoukh',
	'Kuwait — Khaitan',
	'Kuwait — Kuwait City',
	'Kuwait — Mahboula',
	'Kuwait — Mangaf',
	'Kuwait — Mubarak Al-Kabeer',
	'Kuwait — Sabah Al Salem',
	'Kuwait — Salmiya',
	'Kuwait — Shuwaikh',
	'Kuwait — Sulaibikhat',
	// Oman (26)
	'Oman — Adam',
	'Oman — Al Buraimi',
	'Oman — Bahla',
	'Oman — Barka',
	'Oman — Bidbid',
	'Oman — Duqm',
	'Oman — Haima',
	'Oman — Ibri',
	'Oman — Ibra',
	'Oman — Khasab',
	'Oman — Liwa',
	'Oman — Masirah',
	'Oman — Mirbat',
	'Oman — Muscat',
	'Oman — Muttrah',
	'Oman — Nizwa',
	'Oman — Qurayyat',
	'Oman — Rustaq',
	'Oman — Saham',
	'Oman — Salalah',
	'Oman — Samail',
	'Oman — Seeb',
	'Oman — Shinas',
	'Oman — Sohar',
	'Oman — Sur',
	'Oman — Thumrait',
	// Qatar (15)
	'Qatar — Abu Samra',
	'Qatar — Al Daayen',
	'Qatar — Al Khor',
	'Qatar — Al Rayyan',
	'Qatar — Al Ruwais',
	'Qatar — Al Shamal',
	'Qatar — Al Wakrah',
	'Qatar — Al Wukair',
	'Qatar — Doha',
	'Qatar — Dukhan',
	'Qatar — Lusail',
	'Qatar — Mesaieed',
	'Qatar — Ras Laffan',
	'Qatar — Umm Salal Ali',
	'Qatar — Umm Salal Mohammed',
	// Saudi Arabia (38)
	'Saudi Arabia — Abha',
	'Saudi Arabia — Abqaiq',
	'Saudi Arabia — Al Bahah',
	'Saudi Arabia — Al Hofuf',
	'Saudi Arabia — Al Jubail',
	'Saudi Arabia — Al Khafji',
	'Saudi Arabia — Al Kharj',
	"Saudi Arabia — Al Majma'ah",
	'Saudi Arabia — Al Qatif',
	'Saudi Arabia — Al Qunfudhah',
	'Saudi Arabia — AlUla',
	'Saudi Arabia — Arar',
	'Saudi Arabia — Bisha',
	'Saudi Arabia — Buraydah',
	'Saudi Arabia — Dammam',
	'Saudi Arabia — Dhahran',
	'Saudi Arabia — Duba',
	'Saudi Arabia — Hafar Al Batin',
	'Saudi Arabia — Hail',
	'Saudi Arabia — Jazan',
	'Saudi Arabia — Jeddah',
	'Saudi Arabia — Khamis Mushait',
	'Saudi Arabia — Khobar',
	'Saudi Arabia — King Abdullah Economic City',
	'Saudi Arabia — Makkah',
	'Saudi Arabia — Madinah',
	'Saudi Arabia — Najran',
	'Saudi Arabia — Rabigh',
	'Saudi Arabia — Rafha',
	'Saudi Arabia — Ras Tanura',
	'Saudi Arabia — Riyadh',
	'Saudi Arabia — Sakaka',
	'Saudi Arabia — Tabuk',
	'Saudi Arabia — Taif',
	'Saudi Arabia — Turaif',
	'Saudi Arabia — Umluj',
	'Saudi Arabia — Unaizah',
	'Saudi Arabia — Yanbu',
	// United Arab Emirates (19)
	'United Arab Emirates — Abu Dhabi',
	'United Arab Emirates — Ajman',
	'United Arab Emirates — Al Ain',
	'United Arab Emirates — Al Dhaid',
	'United Arab Emirates — Al Ruwais',
	'United Arab Emirates — Dibba Al-Fujairah',
	'United Arab Emirates — Dibba Al-Hisn',
	'United Arab Emirates — Dubai',
	'United Arab Emirates — Fujairah',
	'United Arab Emirates — Ghayathi',
	'United Arab Emirates — Hatta',
	'United Arab Emirates — Jebel Ali',
	'United Arab Emirates — Kalba',
	'United Arab Emirates — Khor Fakkan',
	'United Arab Emirates — Liwa',
	'United Arab Emirates — Madinat Zayed',
	'United Arab Emirates — Ras Al Khaimah',
	'United Arab Emirates — Sharjah',
	'United Arab Emirates — Umm Al Quwain',
	// Europa (04/09): la clienta recluta a gente que vive en Europa y busca
	// trabajo solo en Dubái. Hasta hoy la única salida era «Otro» con texto
	// libre, y ese valor no cruza con ninguna vacante.
	'Spain — Madrid',
	'Spain — Barcelona',
	'Spain — Valencia',
	'Spain — Sevilla',
	'Spain — Málaga',
	'Spain — Marbella',
	'Spain — Palma de Mallorca',
	'Spain — Ibiza',
	'Spain — Tenerife',
	'Spain — Las Palmas',
	'Spain — Bilbao',
	'Portugal — Lisbon',
	'Portugal — Porto',
	'Portugal — Faro',
	'France — Paris',
	'France — Nice',
	'France — Cannes',
	'France — Lyon',
	'France — Marseille',
	'France — Bordeaux',
	'Italy — Rome',
	'Italy — Milan',
	'Italy — Florence',
	'Italy — Venice',
	'Italy — Naples',
	'Germany — Berlin',
	'Germany — Munich',
	'Germany — Frankfurt',
	'Germany — Hamburg',
	'United Kingdom — London',
	'United Kingdom — Manchester',
	'United Kingdom — Edinburgh',
	'United Kingdom — Birmingham',
	'Ireland — Dublin',
	'Netherlands — Amsterdam',
	'Belgium — Brussels',
	'Switzerland — Geneva',
	'Switzerland — Zurich',
	'Switzerland — Lausanne',
	'Austria — Vienna',
	'Greece — Athens',
	'Greece — Thessaloniki',
	'Greece — Mykonos',
	'Greece — Santorini',
	'Turkey — Istanbul',
	'Turkey — Antalya',
	'Cyprus — Limassol',
	'Cyprus — Paphos',
	'Malta — Valletta',
	'Monaco — Monaco',
	'Poland — Warsaw',
	'Poland — Kraków',
	'Czech Republic — Prague',
	'Hungary — Budapest',
	'Sweden — Stockholm',
	'Denmark — Copenhagen',
	'Norway — Oslo',
	'Finland — Helsinki',
	'Croatia — Dubrovnik',
	'Croatia — Split',
	'Romania — Bucharest',
	'Bulgaria — Sofia',
	'Serbia — Belgrade',
	'Slovakia — Bratislava',
	'Luxembourg — Luxembourg',
	'Other',
] as const;

// ============================================
// COMPANY CATALOGS (get-a-quote-form.md)
// ============================================

export const PROPERTY_TYPES = [
	'Hotels and Resorts',
	'Beach Clubs',
	'Fine Dining Restaurants',
	'Lifestyle Hospitality Brands',
	'Private Members Clubs',
	'Multi-Location Hospitality Groups',
	'Other',
] as const;

/**
 * Del «tipo de propiedad» que marca la empresa en el alta al «tipo de negocio»
 * con el que puntúa el baremo (04/09).
 *
 * Son dos catálogos distintos y hasta hoy nadie traducía uno en otro: el
 * baremo buscaba `Luxury Hotel` y la ficha decía `Hotels and Resorts`, así
 * que ningún tipo se reconocía y los cuatro criterios automáticos caían al
 * suelo de 5 — 30 puntos para TODAS las empresas, que es lo que la clienta
 * veía. El tipo validado por Kairos sigue mandando cuando existe; esto es lo
 * que vale mientras nadie lo ha validado.
 *
 * `multiLocation`: un restaurante con varios locales (o que pertenece a un
 * grupo) es `Restaurant Group`, no un independiente.
 */
export function businessTypeOfPropertyType(
	propertyType: string | null | undefined,
	opts: { multiLocation?: boolean } = {},
): string | null {
	switch (propertyType) {
		case 'Hotels and Resorts':
			return 'Luxury Hotel';
		case 'Beach Clubs':
			return 'Beach Club';
		case 'Fine Dining Restaurants':
			return opts.multiLocation ? 'Restaurant Group' : 'Independent Restaurant';
		case 'Lifestyle Hospitality Brands':
		case 'Private Members Clubs':
			return 'Lifestyle Brand';
		case 'Multi-Location Hospitality Groups':
			return 'Hospitality Group';
		default:
			return null;
	}
}

/**
 * Plan the lead says it is interested in, as a LABEL — it is what the quote
 * form shows and what `company_leads.plan_of_interest` stores. It also keys the
 * company-scoring weights (`subscriptionByPlan`), so renaming an entry here
 * without renaming it there silently scores that plan at zero.
 */
export const PLANS_OF_INTEREST = ['Access', 'Professional', 'Prime', 'Enterprise'] as const;

/** Lead source options for the company edit dropdown (display labels via i18n). */
export const LEAD_SOURCES = ['linkedin', 'apollo', 'manual', 'referral', 'website', 'sticky-panel'] as const;

// ============================================
// VALIDATION (admin) — candidate
// ============================================

/**
 * Candidate lead lifecycle status — the **six states** of MD-Scoring-Candidatos §2,
 * under the names this codebase already had. The mapping matters, because the MD
 * writes them in Spanish and the DB does not:
 *
 * | MD (§2)                   | here                        | acceso  | CRM |
 * |---------------------------|-----------------------------|---------|-----|
 * | `procesando`              | `pending_completion`        | no      | no  |
 * | `completado`              | `completed`                 | no      | no  |
 * | `pendiente_video`         | `pending_video`             | LIMITADO| no  |
 * | `aprobado`                | `approved`                  | sí      | sí  |
 * | `aprobado_observaciones`  | `approved_with_observations`| no      | no  |
 * | `rechazado`               | `rejected`                  | no      | sí  |
 *
 * The four original names are kept ON PURPOSE: renaming `approved` to `completed`
 * to match the MD's wording would rewrite every existing row and every query that
 * reads them, and `db push` renames by DROP + CREATE (see CLAUDE.md § Migraciones).
 * The two new ones are additive, which is the only shape that is safe here.
 *
 * `pending_completion` is where a scored candidate waits for their files; the
 * completion email/link goes out automatically and the rules pre-filter NEVER
 * auto-approves from here. `completed` means the files are in and a human has yet
 * to decide.
 */
/**
 * `CandidateLead.source` of the people brought over from the previous software
 * (10/09). They arrive already `approved` but with the wizard never submitted,
 * and that combination is what holds them to "complete your profile" before
 * the portal opens — see `candidate-access.util.ts`. The importer writes it;
 * the gate reads it. Same string in both places, or the gate matches nobody.
 */
export const MIGRATED_LEAD_SOURCE = 'migration';

/**
 * `CandidateLead.source` of whoever signs up through `/candidate-signup`. The
 * short form wrote it; the wizard that replaced it (23/09) keeps writing it, so
 * the reports that group by origin do not break in two.
 */
export const PUBLIC_SIGNUP_SOURCE = 'public-form';

/**
 * `CandidateLead.status` of a public signup that has started and not been sent
 * (23/09). The wizard saves step by step, so the row exists from the first file
 * — but nobody should see it until the person presses «Submit»: not the Scoring
 * queue, not the candidate list, not a dashboard count.
 *
 * Deliberately NOT in `CANDIDATE_STATUSES`: that list feeds filters and labels
 * the team picks from, and this is not a state the team works with. The queries
 * that list leads compose `SUBMITTED_LEAD` (backend) so it never shows up; a
 * draft untouched for a week is deleted, files and all.
 */
export const CANDIDATE_SIGNUP_DRAFT_STATUS = 'signup_draft';

export const CANDIDATE_STATUSES = [
	'pending_completion',
	'completed',
	'approved',
	'approved_with_observations',
	'rejected',
	/**
	 * LEGACY (client ruling of 12-ago). `pending_video` was an approval state
	 * that let a candidate in with limited access until they delivered a video.
	 * The client removed it: «"Aprobado" da acceso completo al perfil y NO debe
	 * depender del vídeo. PENDING VIDEO no debe ser un estado de aprobación».
	 *
	 * Nothing writes it any more and the seeder migrates the rows that had it to
	 * `approved`. It stays listed so a row that escaped the migration still
	 * renders with a label instead of a raw key.
	 */
	'pending_video',
] as const;

/** The statuses a candidate can be in while nobody has resolved them yet. */
export const CANDIDATE_PENDING_STATUSES = ['pending_completion', 'completed', 'approved_with_observations'] as const;

/**
 * Un candidato con decisión firmada (12/09). La leen las dos puertas que tienen
 * que estar de acuerdo: la cola de scoring, que esconde los botones de decidir
 * —volver a decidir reenvía el acceso, repite el CRM y, en rechazo, el correo y
 * el baneo—, y la corrección del K-Score del servidor, que sin decisión previa
 * no tiene nada que corregir.
 *
 * `approved_with_observations` NO está aquí (13/09): es un estado ABIERTO —lo
 * dice `CANDIDATE_PENDING_STATUSES`— que existe para que alguien lo resuelva a
 * aprobado o rechazado. Contarlo como decidido escondía esos botones y dejaba al
 * candidato sin salida (Avani Moonilall en producción, 13/09).
 *
 * Tipado como lista de cadenas a propósito: lo que llega de la API es un string.
 */
export const CANDIDATE_DECIDED_STATUSES: readonly string[] = ['approved', 'rejected'];

/**
 * The approval buttons of §4 plus the rejection, as the API names them —
 * `approved` is "luxury ready".
 * `approved_with_observations` is a REAL state now (it was only ever a label on
 * the score before), because the MD hangs behaviour off it: no email, no CRM, and
 * a mandatory comment that the queue shows as a tooltip.
 *
 * `approved_pending_video` is gone (12-ago): approving no longer depends on the
 * video. A request accepted from an older client is treated as a plain
 * `approved`, not refused — see `CandidateService.validate`.
 */
export const CANDIDATE_DECISIONS = ['approved', 'approved_with_observations', 'rejected'] as const;

/**
 * Which button the approval came from (`approved_type` in the MD §3). It survives
 * the transition, so the file can say "aprobado luxury ready" months later, when
 * the status alone no longer remembers which route it took.
 *
 * `pending_video` is LEGACY, same ruling as the status of that name.
 */
export const CANDIDATE_APPROVAL_TYPES = ['luxury_ready', 'observations', 'pending_video'] as const;

/**
 * Where the presentation video stands (`video_status` in the MD §3).
 *
 * Fully INDEPENDENT of the approval since 12-ago: «Si una empresa solicita
 * vídeo, se gestiona como un estado independiente del vídeo: Requested →
 * Uploaded → Validated/Available. Un candidato puede estar APPROVED aunque no
 * tenga vídeo». So this ladder decides what the video is worth and never what
 * the candidate is allowed to do.
 *
 * `requested` is the ask; `no_video` is the resting state of someone nobody has
 * asked. They are different on purpose — the first is a promise outstanding.
 */
export const CANDIDATE_VIDEO_STATUSES = ['no_video', 'requested', 'uploaded', 'validated', 'insufficient'] as const;

/**
 * What the OUTSIDE (company, candidate) is told about the presentation video's
 * review — a projection of `CANDIDATE_VIDEO_STATUSES` (03/09 · punto 13).
 * `validated` is the only state in which a company can play it: until then it
 * is `pending`, and after an insufficient verdict it is gone for companies
 * until the candidate uploads a new one. Null = there is no video.
 */
export const VIDEO_REVIEW_STATES = ['pending', 'validated', 'insufficient'] as const;
export type VideoReviewState = (typeof VIDEO_REVIEW_STATES)[number];

/** Internal labels (admin-only). */
export const INTERNAL_LABELS = [
	'luxury_ready',
	'high_potential',
	'needs_training',
	'sensitive_profile',
	'watchlist',
] as const;

/** The 6 validation blocks (triple-state) per candidate. */
export const VALIDATION_BLOCK_KEYS = ['identity', 'legal', 'languages', 'experience', 'education', 'video'] as const;

/** Triple state of each validation block. */
export const VALIDATION_BLOCK_STATES = ['approved', 'approved_with_observations', 'rejected'] as const;

// ============================================
// VALIDATION (admin) — company
// ============================================

/** Company validation states. `processing` = scoring running (async), then the
 *  lead moves to `pending` for human review. */
export const COMPANY_VALIDATION_STATES = ['processing', 'pending', 'approved', 'approved_with_limits'] as const;

/** Company decision the validator can set. */
export const COMPANY_DECISIONS = ['approved', 'approved_with_limits', 'rejected'] as const;

// ============================================
// VACANCIES (M06)
// ============================================

/** Hospitality departments for vacancies. */
/** Systems/tools of the vacancy criteria and the candidate profile (§5.4). */
export const SYSTEMS = ['Opera PMS', 'Micros', 'POS', 'SAP', 'SevenRooms', 'OpenTable', 'Excel', 'Other'] as const;

export const DEPARTMENTS = [
	'Front Office',
	'F&B Service',
	'Culinary',
	'Housekeeping',
	'Spa & Wellness',
	'Sales & Marketing',
	'Management',
	'Other',
] as const;

/** Vacancy lifecycle. */
// `paused` RETIRADO (MD-Vacante-Modelo-Creacion, revisión del 10-ago: «no se
// pueden pausar vacantes»). Una vacante vive 30 días y se cierra; pausarla era
// una forma de congelar el reloj y quedarse el cupo del plan sin publicar.
export const VACANCY_STATUSES = ['draft', 'active', 'closed'] as const;

/**
 * Application pipeline stages. M06 only creates applications at `received`;
 * transitions are managed by the pipeline (M07).
 */
export const APPLICATION_STATUSES = [
	'received',
	'cv_read',
	'in_review',
	'interview',
	'selected',
	'declined',
	// El candidato retiró su candidatura (C-P1-03, 02/09). Se comporta como
	// `declined` en tableros y contadores; la diferencia es QUIÉN cerró.
	'withdrawn',
] as const;

/**
 * Quién abrió la candidatura (clienta, 03/09 · punto 10): `self` el candidato
 * se inscribió, `invited` la empresa lo invitó, `kairos` la creó una entrega
 * del Private Talent Desk. `shortlisted` dice que Kairos la CURÓ; esto dice
 * quién la creó — quien se inscribió solo puede acabar también en la
 * selección. Sin esto, el candidato leía «aplicaste» a una vacante que le
 * propuso Kairos y la empresa no distinguía una selección de una solicitud.
 */
export const APPLICATION_ORIGINS = ['self', 'invited', 'kairos'] as const;
export type ApplicationOrigin = (typeof APPLICATION_ORIGINS)[number];

// El requisito de visado de la vacante ya NO tiene lista propia: comparte
// `VISA_OPTIONS` con el candidato (spec del 25/08). Ahí está el porqué.

/**
 * Subscription plans, flattened to the single-mode shape the pre-Monthly/Annual
 * callers expect: one price and one set of limits per plan.
 *
 * DERIVED, not written by hand. The commercial model lives in
 * `plans.catalog.ts` and this view is its ANNUAL mode — the mode every company
 * signed on so far, and the one the old constant's numbers came from. Keeping a
 * second hand-typed list is how the catalog and the pricing screen drifted apart
 * the last time.
 *
 * Both this and `plans.catalog.ts` are only the seed and the unseeded-database
 * fallback; the `plans` table is the authority (see `PlanCatalogService`).
 */
export const SUBSCRIPTION_PLANS: readonly {
	key: string;
	name: string;
	priceAed: number;
	recommended: boolean;
	activeRoles: number | null;
	annualRoles: number;
	videosPerMonth: number | null;
	replacementDays: number;
	earlyAccessWindow: boolean;
}[] = PLAN_CATALOG.map((plan) => {
	const annual = plan.pricing.annual;
	return {
		key: plan.key,
		name: plan.name,
		priceAed: annual.priceAed ?? 0,
		recommended: plan.recommended,
		activeRoles: annual.entitlements.activeVacancies,
		annualRoles: annual.entitlements.vacancyCredits ?? 0,
		videosPerMonth: annual.entitlements.videoRequests,
		replacementDays: annual.entitlements.replacementDays,
		earlyAccessWindow: annual.entitlements.priority === 'first',
	};
});

// `pending_documents` (04/09): plan pagado cuya documentación corporativa aún
// no ha verificado Kairos. La fila existe y el cobro es real, pero el plan no
// rige — `resolveCompanyPlanKey` lo lee como freemium — hasta que los papeles
// se aprueban (revisión de PTD o aprobación de la empresa), y entonces empieza
// a contar el periodo.
export const SUBSCRIPTION_STATUSES = ['active', 'pending_documents', 'past_due', 'canceled', 'trial'] as const;

/**
 * Add-on catalog, flattened the same way: the monthly price, or the annual one
 * for the packs sold only on an annual term. Derived from `ADDON_CATALOG`.
 */
export const SUBSCRIPTION_ADDONS: readonly { key: string; name: string; priceAed: number }[] = ADDON_CATALOG.map(
	(addon) => ({
		key: addon.key,
		name: addon.name,
		priceAed: addon.priceAed ?? addon.priceAnnualAed ?? 0,
	}),
);
